/**
 * /videos/conditions — Conditions de contribution pour les Shorts.
 *
 * Document juridique couvrant 10 sections :
 *  1. Objet
 *  2. Qui peut contribuer
 *  3. Droits d'auteur (CPI L111-1, L122-4)
 *  4. Droit à l'image (art. 9 Code civil)
 *  5. Mineurs & accord parental
 *  6. Licence accordée à Code 2 Roues
 *  7. Modération & décision discrétionnaire
 *  8. Contenus interdits (dont wheeling, rodéos, excès)
 *  9. Signalement & LCEN art. 6
 *  10. RGPD, durée, droits
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import useSeo from '../hooks/useSeo';

export default function VideosConditionsPage() {
  useSeo({
    title: 'Conditions de contribution Shorts — Code 2 Roues',
    description: 'Droits d\'auteur, droit à l\'image, licence et modération des vidéos communautaires Code 2 Roues.',
    canonical: 'https://code2roues.fr/videos/conditions',
  });

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="videos-conditions-page">
      <Link to="/shorts/proposer" className="inline-flex items-center text-xs uppercase tracking-widest text-[hsl(var(--primary))] font-black">
        <ArrowLeft className="w-3 h-3 mr-1" /> Retour au formulaire
      </Link>
      <h1 className="text-3xl md:text-4xl font-extrabold mt-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        Conditions de contribution — Shorts
      </h1>
      <p className="text-sm text-foreground/55 mt-2 italic">
        Dernière mise à jour : octobre 2026. Document rédigé par l'équipe Code 2 Roues.
      </p>

      <article className="prose dark:prose-invert prose-sm mt-8 max-w-none">
        <h2>1. Objet</h2>
        <p>
          Les présentes conditions encadrent la soumission de vidéos par les membres sur la plateforme
          Code 2 Roues (ci-après « le Site »). Elles s'ajoutent aux Conditions Générales d'Utilisation du Site
          et prévalent sur tout autre document pour ce qui concerne les contenus utilisateurs.
        </p>

        <h2>2. Qui peut contribuer</h2>
        <p>
          La soumission est réservée aux membres connectés disposant d'un compte Code 2 Roues actif.
          Elle n'implique aucune rémunération. Code 2 Roues se réserve le droit, à tout moment,
          de suspendre les droits de contribution d'un membre en cas de manquement.
        </p>

        <h2>3. Droits d'auteur</h2>
        <p>
          En soumettant une vidéo, vous garantissez en être l'auteur ou disposer de l'ensemble des
          autorisations nécessaires auprès de l'auteur et des titulaires de droits voisins
          (article L.111-1 du Code de la propriété intellectuelle). Vous reconnaissez que la mise à
          disposition d'une œuvre sans autorisation constitue une contrefaçon
          (article L.122-4 CPI) susceptible de poursuites pénales et civiles.
        </p>

        <h2>3 bis. Partage d'un lien tiers (TikTok, YouTube, Instagram, Vimeo…)</h2>
        <p>
          Vous pouvez proposer le <strong>lien</strong> d'une vidéo publiée sur une plateforme tierce
          (TikTok, YouTube, YouTube&nbsp;Shorts, Instagram Reels, Vimeo, Dailymotion). Dans ce cas :
        </p>
        <ul>
          <li>
            <strong>La proposition d'un lien ne vaut pas autorisation automatique de republication.</strong>
            Code 2 Roues vérifiera, avant mise en avant, les conditions d'intégration propres à chaque
            plateforme (TikTok Developer Terms, YouTube API Services Terms, Meta Platform Terms, etc.).
          </li>
          <li>
            Si vous n'êtes <strong>pas</strong> l'auteur de la vidéo, assurez-vous qu'elle est publiée
            publiquement et que son auteur n'a pas désactivé l'embed. Une vidéo rendue privée ou
            désindexée sera retirée automatiquement de la rubrique Shorts.
          </li>
          <li>
            Si vous souhaitez soumettre votre <strong>propre vidéo</strong> (téléversement direct),
            une <strong>autorisation de diffusion spécifique</strong> pourra vous être demandée par
            écrit avant publication, en complément de la licence définie à la section&nbsp;6.
          </li>
          <li>
            Code 2 Roues n'héberge pas les vidéos tierces : elles restent lues depuis la plateforme
            d'origine via son lecteur officiel. Les éventuelles atteintes au droit d'auteur commises
            par un contributeur partageant un lien tiers n'engagent que la responsabilité de ce
            dernier (article&nbsp;6 LCEN).
          </li>
        </ul>

        <h2>4. Droit à l'image</h2>
        <p>
          Vous garantissez disposer de l'autorisation écrite de chaque personne identifiable
          apparaissant dans la vidéo (article 9 du Code civil). Les plaques d'immatriculation
          lisibles doivent être floutées. Les visages des mineurs ne peuvent apparaître
          qu'avec l'accord exprès des représentants légaux (cf. section suivante).
        </p>

        <h2>5. Mineurs & accord parental</h2>
        <p>
          Si vous êtes mineur, votre contribution requiert l'accord écrit préalable d'un parent
          ou tuteur légal, à envoyer à{' '}
          <a href="mailto:contact@code2roues.fr">contact@code2roues.fr</a> avec le lien de la
          proposition. Toute vidéo faisant apparaître un mineur sans accord parental sera refusée.
        </p>

        <h2>6. Licence accordée à Code 2 Roues</h2>
        <p>
          En soumettant une vidéo, vous accordez à Code 2 Roues une <strong>licence gratuite,
          mondiale, non exclusive et limitée à la diffusion sur le Site</strong> (y compris sa
          version mobile, ses applications et ses communications éditoriales directes).
          <strong> Vous restez propriétaire de l'œuvre.</strong> Cette licence est accordée pour
          la durée de publication, et prend fin automatiquement en cas de retrait de la vidéo.
          Vous pouvez demander son retrait à tout moment en écrivant à{' '}
          <a href="mailto:contact@code2roues.fr">contact@code2roues.fr</a> ; le retrait sera
          effectif sous <strong>7 jours ouvrés</strong>.
        </p>

        <h2>7. Modération & décision discrétionnaire</h2>
        <p>
          Chaque proposition est soumise à modération a priori. Code 2 Roues accepte, refuse ou
          édite les contributions à sa seule discrétion, sans obligation de motivation détaillée.
          La publication n'est jamais garantie. Un refus ne constitue pas une appréciation sur
          la personne du contributeur.
        </p>

        <h2>8. Contenus interdits</h2>
        <p>
          Sont strictement interdits et seront refusés (ou retirés) sans préavis :
        </p>
        <ul>
          <li>Les vidéos valorisant ou démontrant des <strong>infractions au Code de la route</strong>
            (excès de vitesse, feu rouge grillé, dépassement par la droite dangereux, etc.) ;</li>
          <li>Les comportements à risque pour autrui : <strong>wheeling, stoppie, acrobaties, rodéos
            urbains, remontée de file en roulant sur la bande d'arrêt d'urgence</strong>, etc. ;</li>
          <li>Les contenus faisant l'apologie de la conduite sans équipement adapté ;</li>
          <li>Les contenus haineux, discriminatoires, à caractère sexuel ou pornographique ;</li>
          <li>Les publicités déguisées, démarchages commerciaux ou liens d'affiliation masqués ;</li>
          <li>Les contenus mettant en scène un mineur sans accord parental documenté ;</li>
          <li>Toute vidéo portant atteinte au droit à l'image ou au droit d'auteur d'un tiers.</li>
        </ul>

        <h2>9. Signalement (LCEN art. 6)</h2>
        <p>
          Chaque vidéo publiée dispose d'un bouton « Signaler » accessible à tous les visiteurs.
          Les signalements sont examinés en priorité. Pour un signalement formel au titre de
          l'article 6 de la Loi pour la confiance dans l'économie numérique (LCEN), écrivez à
          <a href="mailto:contact@code2roues.fr"> contact@code2roues.fr</a> en précisant :
          le lien de la vidéo, la nature des faits litigieux, votre identité (et vos coordonnées
          pour les personnes morales).
        </p>

        <h2>10. Protection des données (RGPD)</h2>
        <p>
          Code 2 Roues collecte les données strictement nécessaires au traitement de votre
          proposition : identifiant membre, pseudonyme, email, titre, description, thème, lien ou
          fichier vidéo, horodatage de l'acceptation des conditions, adresse IP (hashée) à des
          fins de sécurité. Finalité : modération et diffusion du contenu. Durée de conservation :
          <strong> 12 mois</strong> après la dernière action sur la vidéo (ou immédiatement en cas
          de retrait). Base légale : exécution contractuelle et intérêt légitime à modérer.
          Vous disposez d'un droit d'accès, de rectification, d'effacement, d'opposition et de
          portabilité, à exercer auprès de{' '}
          <a href="mailto:contact@code2roues.fr">contact@code2roues.fr</a>. Vous avez également
          le droit d'introduire une réclamation auprès de la CNIL.
        </p>

        <hr />
        <p className="text-xs text-foreground/55">
          <FileText className="w-3 h-3 inline mr-1" />
          En soumettant une vidéo, vous cochez une case explicite valant acceptation complète du présent
          document. Cette acceptation est horodatée et conservée dans nos registres comme preuve.
        </p>
      </article>
    </div>
  );
}
